import { useAuth } from "../../hooks/useAuth";
import type { SessionState } from "./coord/messages";

/**
 * Maps the opener's auth state to the `SessionState` the viewer gates content
 * on (OR-22558). Pure so it can be unit-tested without the auth hook.
 *
 * This reflects *auth* validity only — `expired` when unauthenticated (the app
 * can't distinguish logout from expiry; both surface as no session),
 * `active` otherwise. VLA-lock ownership is a separate concern, conveyed via
 * `heldByMe` (see `useDocumentBroadcaster`): losing/never holding the lock no
 * longer blanks the document, it only hides the move/check-in toolbar.
 * @param {boolean} isAuthenticated - Whether the session is authenticated.
 * @returns {SessionState} The session signal to broadcast.
 */
export function deriveSessionState(isAuthenticated: boolean): SessionState {
  return isAuthenticated ? "active" : "expired";
}

/**
 * Live `SessionState` for the opener window, derived from `useAuth`. Document
 * windows gate their content on this; VLA-lock ownership (which gates only
 * the toolbar) is broadcast separately as `heldByMe`.
 * @returns {SessionState} The current session signal.
 */
export function useViewerSessionState(): SessionState {
  const { isAuthenticated } = useAuth();
  return deriveSessionState(isAuthenticated);
}
